<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { collection, onSnapshot, addDoc, updateDoc, doc, query, orderBy } from 'firebase/firestore';
  import Button from '../ui/Button.svelte';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';
  import Modal from '../ui/Modal.svelte';
  import ApiKeyModal from './ApiKeyModal.svelte';
  import type { ApiKey, ApiKeyScope } from '../../lib/types';

  type Props = {
    businessId: string | null;
  };

  let { businessId }: Props = $props();

  let apiKeys = $state<ApiKey[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let showCreateModal = $state(false);
  let showRevealModal = $state(false);
  let revealedKey = $state<string | null>(null);
  let revealedKeyName = $state('');
  let copiedToClipboard = $state(false);

  let activeKeys = $derived(apiKeys.filter(k => k.status === 'active'));

  // Firestore listener
  $effect(() => {
    if (!businessId) {
      loading = false;
      return;
    }

    const q = query(
      collection(db, 'businessdata', businessId, 'apiKeys'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      apiKeys = snap.docs.map(d => ({ id: d.id, ...d.data() } as ApiKey));
      loading = false;
    }, (err) => {
      console.error('Error loading API keys:', err);
      error = 'Failed to load API keys';
      loading = false;
    });

    return () => unsub();
  });

  function generateKey(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'eq_';
    for (let i = 0; i < 40; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  }

  function maskKey(key: string): string {
    return key.substring(0, 3) + '****' + key.substring(key.length - 4);
  }

  async function handleCreate(name: string, scopes: ApiKeyScope[]) {
    if (!businessId || !authState.user) return;

    const fullKey = generateKey();
    const keyPrefix = maskKey(fullKey);

    try {
      await addDoc(collection(db, 'businessdata', businessId, 'apiKeys'), {
        name,
        keyPrefix,
        scopes,
        createdAt: new Date().toISOString(),
        createdBy: authState.user.uid,
        lastUsedAt: null,
        status: 'active',
      });

      showCreateModal = false;
      revealedKey = fullKey;
      revealedKeyName = name;
      showRevealModal = true;
    } catch (err: any) {
      error = `Failed to create API key: ${err.message}`;
    }
  }

  async function handleRevoke(key: ApiKey) {
    if (!businessId) return;

    const confirmed = window.confirm(
      `Are you sure you want to revoke the API key "${key.name}"? This action cannot be undone and any applications using this key will stop working.`
    );
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, 'businessdata', businessId, 'apiKeys', key.id), {
        status: 'revoked',
      });
    } catch (err: any) {
      error = `Failed to revoke API key: ${err.message}`;
    }
  }

  async function copyKey() {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      copiedToClipboard = true;
      setTimeout(() => {
        copiedToClipboard = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  }
</script>

<section class="section">
  <div class="section-header">
    <div>
      <h2>API Keys</h2>
      <p class="section-description">Manage API keys for programmatic access to your EQUOS data.</p>
    </div>
    <Button variant="primary" onclick={() => showCreateModal = true} disabled={!businessId}>
      Create API Key
    </Button>
  </div>

  {#if error}
    <div class="error-banner">
      <span>{error}</span>
      <button class="error-dismiss" onclick={() => error = null}>
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <LoadingSpinner />
      <p>Loading API keys...</p>
    </div>
  {:else if activeKeys.length === 0}
    <div class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
        </svg>
      </div>
      <h3>No API Keys</h3>
      <p>Create an API key to access your EQUOS data programmatically.</p>
    </div>
  {:else}
    <div class="api-keys-list">
      {#each activeKeys as key (key.id)}
        <div class="api-key-row">
          <div class="key-info">
            <span class="key-name">{key.name}</span>
            <code class="key-prefix">{key.keyPrefix}</code>
          </div>
          <div class="key-meta">
            <span class="key-date">Created {formatDate(key.createdAt)}</span>
            <span class="key-scopes">{key.scopes.length} {key.scopes.length === 1 ? 'scope' : 'scopes'}</span>
          </div>
          <Button variant="danger" size="small" onclick={() => handleRevoke(key)}>
            Revoke
          </Button>
        </div>
      {/each}
    </div>
  {/if}
</section>

{#if showCreateModal}
  <ApiKeyModal
    onClose={() => showCreateModal = false}
    onCreate={handleCreate}
  />
{/if}

{#if showRevealModal && revealedKey}
  <Modal isOpen={true} title="API Key Created" size="md" onClose={() => { showRevealModal = false; revealedKey = null; }}>
    <div class="key-reveal">
      <div class="warning-banner">
        <svg viewBox="0 0 20 20" fill="currentColor" style="width:20px;height:20px;flex-shrink:0">
          <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
        </svg>
        <span>Copy your API key now. You won't be able to see it again.</span>
      </div>
      <div class="key-display">
        <label>Key Name</label>
        <p class="key-name-display">{revealedKeyName}</p>
        <label>API Key</label>
        <div class="copy-field">
          <input type="text" value={revealedKey} readonly class="key-input" />
          <Button variant="secondary" size="small" onclick={copyKey}>
            {copiedToClipboard ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </div>
    </div>
    {#snippet actions()}
      <Button variant="primary" onclick={() => { showRevealModal = false; revealedKey = null; }}>Done</Button>
    {/snippet}
  </Modal>
{/if}

<style>
  .section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: #333;
  }

  .section-description {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }

  .error-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    color: #991b1b;
    font-size: 0.9rem;
  }

  .error-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: #991b1b;
    padding: 0;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .error-dismiss:hover {
    opacity: 0.7;
  }

  .error-dismiss svg {
    width: 16px;
    height: 16px;
  }

  /* API Keys List */
  .api-keys-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .api-key-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .key-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .key-name {
    font-weight: 600;
    color: #333;
    font-size: 0.95rem;
  }

  .key-prefix {
    font-family: 'SF Mono', 'Fira Code', 'Fira Mono', monospace;
    font-size: 0.8rem;
    color: #666;
    background: #e5e7eb;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    width: fit-content;
  }

  .key-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .key-date {
    font-size: 0.8rem;
    color: #6b7280;
  }

  .key-scopes {
    font-size: 0.75rem;
    color: #9ca3af;
  }

  /* Key Reveal Modal */
  .key-reveal {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .warning-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    padding: 0.875rem 1rem;
    color: #92400e;
    font-size: 0.9rem;
  }

  .key-display {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .key-display label {
    font-weight: 500;
    color: #374151;
    font-size: 0.85rem;
  }

  .key-name-display {
    margin: 0 0 0.5rem;
    color: #333;
    font-weight: 600;
  }

  .copy-field {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
  }

  .key-input {
    flex: 1;
    font-family: 'SF Mono', 'Fira Code', 'Fira Mono', monospace;
    font-size: 0.85rem;
    padding: 0.625rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #f9fafb;
    color: #333;
    min-width: 0;
  }

  .key-input:focus {
    outline: none;
    border-color: #667eea;
  }

  /* Empty state icon */
  .empty-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    color: #ccc;
  }

  .empty-icon svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 640px) {
    .api-key-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .key-meta {
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
    }

    .copy-field {
      flex-direction: column;
    }
  }
</style>
